import { AnimatePresence, Reorder, useDragControls } from "framer-motion";
import {
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  HighlightContext,
  HostInfoObjectContext,
  renderData,
} from "../UiRenderer";
import {
  appendPath,
  getId,
  getObjectIdFromPathList,
  getPointedPath,
  isFakeObj,
  isHighlighted,
  prettierTitle,
  recoverPath,
} from "../utils";
import { cn } from "../../../utils";
import isEqual from "react-fast-compare";
import { useSchema } from "../../../hooks/useStore";
import { ViewContext } from "../View";
import { DbRenderer } from "./Database";

const getListItemPath = (path, id) => {
  return appendPath(path, `[${id}]`);
};

const getListItemUid = (infoObjectName, listPath, obj, index) => {
  if (isFakeObj(obj) || listPath.includes("*")) {
    listPath = recoverPath(listPath, obj.__path);
  }

  return `${infoObjectName}.${getListItemPath(listPath, getId(obj) ?? index)}`;
};

export const L = Object.freeze({
  minimized: "M",
  preview: "P",
  fullExpanded: "F",
});

const caseListRenderingType = (
  isViewOrFloatingWindow,
  isDropdown,
  listSchema,
  listOptions
) => {
  if (isViewOrFloatingWindow) return L.fullExpanded;
  if (isDropdown) return L.preview;
  // if (listOptions.hideKeys) return L.minimized;

  if (listSchema.render === "summary") return L.minimized;
  if (listSchema.render === "expanded") return L.preview;

  return L.preview; // ?
};

export const ListRenderer = memo((props) => {
  const {
    infoObjectName,
    path,
    data,
    name,
    schema: listSchema,
    options,
    is,
  } = props;

  const isViewOrFloatingWindow = is.view || is.floatingWindow;

  // ! how should we render the list? F, P, M
  const listRenderingType = useMemo(
    () =>
      caseListRenderingType(
        isViewOrFloatingWindow,
        is.previewView,
        listSchema,
        options
      ),
    [isViewOrFloatingWindow, is.previewView, listSchema, options]
  );
  const isListPreview = listRenderingType === L.preview;

  const { rendered } = useContext(ViewContext);
  const isChildView = is.view && path !== "";

  const initOrderedItemsUid = data.map((item, index) => {
    return getListItemUid(infoObjectName, path, item, index);
  });

  const [orderedItemsUid, setOrderedItemsUid] = useState(initOrderedItemsUid);

  const setOrder = useCallback((newOrder) => {
    // TODO update actual data
    setOrderedItemsUid(newOrder);
  }, []);

  useEffect(() => {
    const newOrderItemsUid = data.map((item, index) =>
      getListItemUid(infoObjectName, path, item, index)
    );

    setOrderedItemsUid(newOrderItemsUid);
  }, [data, infoObjectName, path]);

  const listItems = useMemo(() => {
    return orderedItemsUid.map((orderedItemUid) => {
      const item = data.find((dataItem, index) => {
        const dataItemUid = getListItemUid(
          infoObjectName,
          path,
          dataItem,
          index
        );

        return dataItemUid === orderedItemUid;
      });

      if (!item) {
        // TODO the re-render happens faster than the useEffect above updating the state
        // console.warn("Missing item", orderedItemUid, path);
        return null;
      }

      const itemIndex = data.indexOf(item);
      const itemId = getId(item) ?? itemIndex;
      const itemUid = getListItemUid(infoObjectName, path, item, itemIndex);

      const itemPath =
        isChildView &&
        !is.floatingWindow &&
        typeof item === "object" &&
        item.__data
          ? recoverPath(path, item.__path) // ?
          : getListItemPath(path, itemId);

      // created during the construction of child views
      const isFakeItem = isChildView && !is.floatingWindow && !!item.__data;

      // ! ListItem
      return (
        <ListItem
          key={itemUid}
          infoObjectName={infoObjectName}
          path={itemPath}
          name={itemId}
          item={isFakeItem ? item.__data : item}
          itemUid={itemUid}
          options={options}
          is={{
            ...is,
            previewView: isListPreview,
          }}
          isFakeItem={isFakeItem}
        />
      );
    });
  }, [
    orderedItemsUid,
    data,
    infoObjectName,
    path,
    isChildView,
    is,
    options,
    isListPreview,
  ]);

  const handleWheel = useCallback(
    (e) => {
      if (isListPreview && e.deltaY) {
        const target = e.currentTarget;
        const isScrollable = target.scrollWidth > target.clientWidth;

        if (isScrollable) {
          // use requestAnimationFrame to smoothly update scroll position
          requestAnimationFrame(() => {
            target.scrollLeft += e.deltaY;
          });
        }
      }
    },
    [isListPreview]
  );

  const listRef = useRef(null);

  useEffect(() => {
    const listElement = listRef.current;

    if (listElement) {
      listElement.addEventListener("wheel", handleWheel, { passive: false });
      return () => {
        listElement.removeEventListener("wheel", handleWheel);
      };
    }
  }, [handleWheel]);

  if (listRenderingType === L.minimized)
    return (
      <DbRenderer
        key={`${infoObjectName}-${path.length > 0 ? path : "ROOT"}`}
        {...props}
      />
    );

  return (
    <div
      className={cn(
        "r-list-container",
        isViewOrFloatingWindow && "r-list-container-view",
        rendered && "r-rendered",
        isListPreview && "r-list-preview-container"
      )}
    >
      {!!name && !options.hideKeys && (
        <div className={cn("r-key", rendered && "r-rendered")}>
          {prettierTitle(name)}
        </div>
      )}
      <div className="r-list-wrapper">
        <Reorder.Group
          layoutScroll
          axis={isListPreview ? "x" : "y"}
          values={orderedItemsUid}
          onReorder={setOrder}
          className={cn(
            "r-list",
            isViewOrFloatingWindow && "r-list-is-view",
            rendered && "r-rendered",
            isListPreview && "r-list-preview"
          )}
          ref={listRef}
        >
          {listItems}
        </Reorder.Group>
      </div>
    </div>
  );
}, isEqual);

/* -------------------------------------------------------------------------- */

const ListItem = memo(
  ({ infoObjectName, path, name, item, itemUid, options, is, isFakeItem }) => {
    const { rendered } = useContext(ViewContext);
    const schema = useSchema(infoObjectName, getPointedPath(path), true);

    const dragControls = useDragControls();
    const itemRef = useRef(null);
    const [targeted, setTargeted] = useState(false);

    const objectId = getObjectIdFromPathList(path);

    const { highlightedInfoObject, setHighlightedInfoObject } =
      useContext(HighlightContext);

    useEffect(() => {
      if (!is.view) return;

      const handleMouseEnter = () => {
        const id = objectId;
        setHighlightedInfoObject({ infoObject: infoObjectName, id });
      };

      const handleMouseLeave = () => {
        setHighlightedInfoObject({
          infoObject: null,
          id: null,
        });
      };

      const element = itemRef.current;
      if (element) {
        element.addEventListener("mouseenter", handleMouseEnter);
        element.addEventListener("mouseleave", handleMouseLeave);
      }

      return () => {
        if (element) {
          element.removeEventListener("mouseenter", handleMouseEnter);
          element.removeEventListener("mouseleave", handleMouseLeave);
        }
      };
    }, [infoObjectName, path, setHighlightedInfoObject, objectId, is.view]);

    const isH = useMemo(() => {
      if (!is.view) return false;
      return isHighlighted(highlightedInfoObject, infoObjectName, objectId);
    }, [highlightedInfoObject, infoObjectName, is.view, objectId]);

    return (
      <AnimatePresence>
        <Reorder.Item
          ref={itemRef}
          key={itemUid}
          value={itemUid}
          className={cn(
            "r-list-item",
            isH && "r-highlighted",
            targeted && "r-targeted",
            rendered && "r-rendered"
          )}
          dragListener={false}
          dragControls={dragControls}
          onPointerDown={(e) => {
            if (
              e.target === itemRef.current ||
              e.target.parentNode === itemRef.current ||
              e.target.parentNode.parentNode === itemRef.current
            ) {
              e.stopPropagation();
              e.preventDefault();
              dragControls.start(e);
            }
          }}
          style={{
            outline:
              isH || targeted ? "2px solid #e48500" : "2px solid transparent",
            transition: "outline 0.2s ease",
          }}
          data-id={objectId}
        >
          <HostInfoObjectContext.Provider value={{ setTargeted }}>
            {renderData(
              infoObjectName,
              path,
              name,
              item,
              schema,
              {
                ...options,
                hideKeys: true,
              },
              {
                ...is,
                view: is.view && isFakeItem,
              }
            )}
          </HostInfoObjectContext.Provider>
        </Reorder.Item>
      </AnimatePresence>
    );
  },
  isEqual
);

/* -------------------------------------------------------------------------- */
/* -------------------------------------------------------------------------- */
/* -------------------------------------------------------------------------- */

export const PanelRenderer = memo(
  ({ infoObjectName, path, name, data, options }) => {
    const [uid, setUid] = useState([infoObjectName]);

    const setOrder = useCallback((newOrder) => {
      setUid(newOrder);
    }, []);

    return (
      <div className={cn("r-list-container", "r-list-container-view")}>
        <Reorder.Group
          layoutScroll
          axis="y"
          values={uid}
          onReorder={setOrder}
          className={cn("r-list", "r-list-is-view")}
        >
          <ListItem
            infoObjectName={infoObjectName}
            path={path}
            name={name}
            item={data}
            itemUid={infoObjectName}
            options={options}
            is={{
              view: true,
              floatingWindow: false,
              previewView: false,
            }}
          />
        </Reorder.Group>
      </div>
    );
  },
  isEqual
);
